import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { CycleStatement } from '../../types/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { ToneMark } from '../Statements/StatementControls';
import BankCloneModal, { BankItem } from './BankCloneModal';

/**
 * The cycle's Statements tab (P2): a snapshot of statements chosen from the
 * unit's bank, like the cycle's forms and stations. Bank edits don't change it.
 * Fixed once the cycle has started (`locked`); the backend enforces the same rule.
 */
const CycleStatementsTab: React.FC<{ cycleId: string; locked: boolean }> = ({ cycleId, locked }) => {
  const qc = useQueryClient();
  const { lang } = useDirection();
  const [pickOpen, setPickOpen] = React.useState(false);
  const navigate = useNavigate();
  const { data: options = [], isLoading: parametersLoading } = useQuery({
    queryKey: ['parameters'],
    queryFn: () => apiService.getParameters(),
  });
  // The link is stored by id; the name comes from the bank (so a rename shows here too), or from
  // the server's name for cycle rows. "Unavailable" only once the bank has loaded without it.
  const parameterName = (id: string | null, known?: string | null) => {
    if (!id) return null;
    return options.find(o => o.id === id)?.name ?? known ?? (parametersLoading ? null : tr('param.unavailable', lang));
  };

  const { data: statements = [] } = useQuery({
    queryKey: ['cycleStatements', cycleId],
    queryFn: () => apiService.getStatementsByCycle(cycleId),
  });

  const { data: bank = [] } = useQuery({
    queryKey: ['statementBank'],
    queryFn: () => apiService.getStatementBank(),
    enabled: pickOpen,
  });

  const assignMutation = useMutation({
    mutationFn: (ids: string[]) => apiService.assignStatementsToCycle(cycleId, ids),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cycleStatements', cycleId] }); setPickOpen(false); },
  });
  const removeMutation = useMutation({
    mutationFn: (assignmentId: string) => apiService.removeStatementFromCycle(cycleId, assignmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cycleStatements', cycleId] }),
  });

  const inCycle = new Set(statements.map(s => s.templateId));
  const bankItems: BankItem[] = bank
    .filter(s => !inCycle.has(s.id))
    .map(s => ({
      id: s.id,
      name: s.text,
      subtitle: [tr(`tone.${s.tone}`, lang), parameterName(s.sharedParameterId)].filter(Boolean).join(' · '),
    }));

  const error = assignMutation.error ?? removeMutation.error;

  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A' }}>{tr('stmt.cycleTitle', lang)}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>{tr('stmt.cycleHint', lang)}</Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => navigate('/statements/builder')}>
            {tr('stmt.editBank', lang)}
          </Button>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setPickOpen(true)} disabled={locked}>
            {tr('stmt.chooseFromBank', lang)}
          </Button>
        </Stack>
      </Box>

      {locked && (
        <Alert icon={<LockIcon fontSize="small" />} severity="info" sx={{ my: 1.5, py: 0, fontSize: '0.8rem' }}>
          {tr('stmt.locked', lang)}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ my: 1.5 }}>{getErrorMessage(error)}</Alert>}

      {statements.length === 0 ? (
        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem', py: 2 }}>{tr('stmt.none', lang)}</Typography>
      ) : (
        <Stack divider={<Box sx={{ borderTop: '1px solid #F1F5F9' }} />}>
          {statements.map((s: CycleStatement) => {
            const pname = parameterName(s.sharedParameterId, s.parameterName);
            return (
              <Stack key={s.assignmentId} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
                <ToneMark tone={s.tone} />
                <Typography sx={{ flex: 1, fontSize: '0.875rem' }} dir="auto">{s.text}</Typography>
                {pname && <Chip label={pname} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />}
                {!locked && (
                  <Tooltip title={tr('stmt.remove', lang)}>
                    <IconButton
                      size="small"
                      onClick={() => removeMutation.mutate(s.assignmentId)}
                      disabled={removeMutation.isPending}
                      sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <BankCloneModal
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title={tr('stmt.chooseFromBank', lang)}
        items={bankItems}
        onConfirm={ids => assignMutation.mutate(ids)}
        loading={assignMutation.isPending}
      />
    </Box>
  );
};

export default CycleStatementsTab;
